import { Card } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox/index";
import MDTypography from "@/components/atoms/MDTypography";
import MultiSelect from "@/components/atoms/MultiSelect";
import {
  PRODUCT_VARIATION_OPTIONS,
  initialValue,
} from "@/components/screens/ProductFormPage/constants";
import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import SkuVariationForm from "@/components/templates/SkuVariationForm";
import {
  defaultPageNumber as pageNumber,
  defaultPageSize as pageSize,
} from "@/constants/pagination";
import useToast from "@/contexts/toast-context";
import useGetProductVariations from "@/hooks/requests/product-variations/use-get-product-variations";
import useFilters from "@/hooks/use-filters";
import IProductVariation from "@/interfaces/variation.interface";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";
import {
  buildUpdatedVariations,
  getRemovedVariationCodes,
  shouldUpdateSkus,
  updateSkusWithRemovedVariations,
} from "./helpers";

type Props = {
  messages: ProductFormPageMessages;
};

function VariationInfo({ messages }: Props) {
  const { filters } = useFilters<IProductVariation>({ initialValue });
  const { data } = useGetProductVariations({ filters, pageNumber, pageSize });
  const { values, errors, setFieldValue } = useFormikContext<ProductFormData>();
  const { showToast } = useToast();

  const hasVariations = data && data?.items.length;

  const handleVariationChange = (value: string[]) => {
    if (values.skus.length > 1 && value.length === 0) {
      showToast({ text: messages.errorMustHaveAtLeastOneVariation, type: "error" });
      return;
    }

    const currentVariationCodes = values.variationCode ? values.variationCode.split(",") : [];
    const removedVariationCodes = getRemovedVariationCodes(currentVariationCodes, value);
    const updatedVariations = buildUpdatedVariations(value, data?.items || []);

    if (shouldUpdateSkus(removedVariationCodes, values.skus)) {
      const updatedSkus = updateSkusWithRemovedVariations(values.skus, removedVariationCodes);
      setFieldValue("skus", updatedSkus);
    }

    setFieldValue("variationCode", value.join(","));
    setFieldValue("variations", updatedVariations);
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex">
          <MDTypography variant="h5" fontWeight={"bold"}>
            {messages.variationTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid minWidth={300} size={{ xs: 12, sm: "auto" }}>
              {hasVariations ? (
                <MultiSelect
                  label=""
                  options={PRODUCT_VARIATION_OPTIONS(messages, data.items)}
                  value={values.variationCode ? values.variationCode.split(",") : []}
                  onChange={handleVariationChange}
                  limit={15}
                  messages={messages}
                  error={errors.variationCode}
                  placeholder={values.variationCode ? "" : messages.noVariationsSelected}
                  disabled={false}
                />
              ) : (
                <MDTypography variant="body2">{messages.noProductVariationOption}</MDTypography>
              )}
            </Grid>
            <Grid size={12}>
              <SkuVariationForm
                messages={messages}
                sku={!values.isEditingSku ? null : values.sku}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default VariationInfo;
